import Phaser from 'phaser';
import type { DiceFace } from '~shared/types';
import { drawDieFace, DIE_SIZE } from './DiceFace';

const ALL_FACES: DiceFace[] = ['infantry', 'infantry', 'armor', 'grenade', 'flag', 'star'];

// Each entry is the half-squish duration (ms). Full flip = 2× this value.
// Starts fast, decelerates — total per die ≈ 1 450 ms.
const FLIP_HALF_DURATIONS = [55, 70, 95, 130, 175, 220];

export class DiceRoller {
  private scene: Phaser.Scene;
  private containers: Phaser.GameObjects.Container[] = [];
  private graphics:   Phaser.GameObjects.Graphics[]  = [];
  private onContainerCreated?: (c: Phaser.GameObjects.Container) => void;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Called for every die Container created by roll() — lets the scene
   *  reparent dynamically-created dice into a fixed HUD layer (mobile). */
  setContainerHook(cb: (c: Phaser.GameObjects.Container) => void): void {
    this.onContainerCreated = cb;
  }

  clear(): void {
    this.containers.forEach(c => { this.scene.tweens.killTweensOf(c); c.destroy(); });
    this.graphics.forEach(g => g.destroy());
    this.containers = [];
    this.graphics   = [];
  }

  /**
   * Animate `faces.length` dice rolling then landing on their final values.
   * `positions` = canvas-space centre of each die (length must equal faces.length).
   * `onComplete` fires after the last die settles.
   */
  roll(
    faces:      DiceFace[],
    positions:  { x: number; y: number }[],
    onComplete: () => void,
  ): void {
    this.clear();
    // Edge case: 0 dice (terrain penalties reduced all dice) — complete immediately
    if (faces.length === 0) { onComplete(); return; }
    let settled = 0;

    faces.forEach((finalFace, i) => {
      const { x, y } = positions[i];

      const gfx = this.scene.add.graphics();
      drawDieFace(gfx, ALL_FACES[Math.floor(Math.random() * ALL_FACES.length)], 0, 0);

      const container = this.scene.add.container(x, y, [gfx]);
      this.containers.push(container);
      this.graphics.push(gfx);
      this.onContainerCreated?.(container);

      // Slight random stagger so dice don't move in perfect lock-step
      const delay = i * 35 + Math.random() * 30;
      this.scene.time.delayedCall(delay, () => {
        this.animateDie(container, gfx, finalFace, () => {
          settled++;
          if (settled === faces.length) onComplete();
        });
      });
    });
  }

  private animateDie(
    container: Phaser.GameObjects.Container,
    gfx:       Phaser.GameObjects.Graphics,
    finalFace: DiceFace,
    onDone:    () => void,
  ): void {
    let flipIdx = 0;
    const total = FLIP_HALF_DURATIONS.length;

    const doFlip = () => {
      if (flipIdx >= total) {
        // Landing bounce: overshoot-and-settle on Y scale
        this.scene.tweens.add({
          targets:  container,
          scaleY:   1.10,
          scaleX:   0.94,
          duration: 80,
          ease:     'Quad.easeOut',
          yoyo:     true,
          onComplete: onDone,
        });
        return;
      }

      const d       = FLIP_HALF_DURATIONS[flipIdx];
      const isLast  = flipIdx === total - 1;

      // Squish toward flat (simulates the die rotating away from viewer)
      this.scene.tweens.add({
        targets:  container,
        scaleY:   0.04,
        scaleX:   1.10,  // slight bulge on X as volume is compressed on Y
        duration: d,
        ease:     'Quad.easeIn',
        onComplete: () => {
          // At minimum-height moment: swap face
          gfx.clear();
          drawDieFace(
            gfx,
            isLast ? finalFace : ALL_FACES[Math.floor(Math.random() * ALL_FACES.length)],
            0, 0,
          );

          // Expand back to full height
          this.scene.tweens.add({
            targets:  container,
            scaleY:   1.0,
            scaleX:   1.0,
            duration: d,
            ease:     'Quad.easeOut',
            onComplete: () => {
              flipIdx++;
              doFlip();
            },
          });
        },
      });
    };

    doFlip();
  }
}
